<template>
  <div class="jackpot-form">
    <drawer-dialog-controls
      class="dialog-controls"
      :is-loading="isLoading"
      :is-disabled="readonly || endDateValidity !== 'ok'"
      :is-pristine="isPristine"
      @close="$emit('close')"
      @save="saveWithFmt"
    >
      {{ dialogTitle }}
    </drawer-dialog-controls>
    <v-container>
      <v-card
        :is-loading="isLoading"
      >
        <v-card-title>
          Settings (Required)
        </v-card-title>
        <v-divider />
        <v-container>
          <v-row>
            <v-col cols="8">
              <select-project
                v-model="localModelValue.entities"
                label="Projects"
                multiple
                :readonly="readonly"
              />
            </v-col>
            <v-col cols="4" class="d-flex justify-end align-center">
              <v-checkbox
                v-model="localModelValue.rules.show_for_guests"
                class="mt-0"
                label="Visible to guests"
                hide-details
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="4">
              <v-text-field
                v-model="localModelValue.name"
                label="Jackpot Name"
                :readonly="readonly"
                hide-details
              />
            </v-col>
            <v-col cols="4">
              <v-select
                v-model="localModelValue.rules.type"
                label="Jackpot Type"
                :items="JACKPOT_TYPE_OPTIONS"
                :readonly="readonly"
                hide-details
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="4">
              <date-picker
                v-model="dateComponents.start.date"
                label="Date Start"
                show-time
                :time-value="dateComponents.start.time"
                :readonly="readonly || ([STATUS_ACTIVE, STATUS_FINISHED].includes(localModelValue.status))"
                :disabled="localModelValue.status === STATUS_FINISHED"
                :hint="getDateHint(true)"
                @time-change="dateComponents.start.time = $event"
              />
            </v-col>
            <v-col cols="4">
              <date-picker
                v-model="dateComponents.end.date"
                label="Date End"
                show-time
                :time-value="dateComponents.end.time"
                :readonly="readonly || (localModelValue.status === STATUS_FINISHED)"
                :disabled="localModelValue.status === STATUS_FINISHED"
                :hint="endDateHint"
                @time-change="dateComponents.end.time = $event"
              />
            </v-col>
          </v-row>
          <v-divider class="mt-3" />
          <v-row
            v-for="(total, index) in localModelValue.rules.prizepool"
            :key="`prizepool-${total.currency}`"
          >
            <v-col cols="4">
              <v-text-field
                v-model="total.amount"
                type="number"
                min="0"
                label="Guaranteed Prize Sum"
                :readonly="readonly"
                hide-details
              />
            </v-col>
            <v-col
              class="d-flex align-end"
              cols="3"
            >
              <v-select
                v-model="total.currency"
                class="undisable-selection"
                label="Currency"
                :items="allCurrencyOptionsVSelectFormatted"
                :disabled="total.currency === 'EUR'"
                :item-disabled="item => selectedPrizepoolCurrencies.includes(item.value)"
                :readonly="readonly"
                hide-details
              />
              <v-btn
                v-if="total.currency !== 'EUR'"
                class="ml-2"
                color="error"
                icon
                :disabled="readonly"
                @click="localModelValue.rules.prizepool.splice(index, 1)"
              >
                <v-icon>mdi-minus-circle-outline</v-icon>
              </v-btn>
              <v-btn
                v-if="!!availablePrizepoolOptions.length"
                class="ml-2"
                color="primary"
                icon
                :disabled="readonly"
                @click="localModelValue.rules.prizepool.push({ amount: 0, currency: availablePrizepoolOptions[0] })"
              >
                <v-icon>
                  mdi-plus-circle-outline
                </v-icon>
              </v-btn>
            </v-col>
          </v-row>
          <v-row>
            <v-col
              cols="12"
              class="pb-0"
            >
              <h4>Trigger</h4>
            </v-col>
            <v-col
              cols="4"
              class="pt-0"
            >
              <v-select
                v-model="localModelValue.rules.draw.period"
                label="Draw Duration"
                :items="DRAW_PERIOD_OPTIONS"
                :readonly="readonly"
                hide-details
              />
            </v-col>
            <v-col
              cols="4"
              class="pt-0"
            >
              <v-text-field
                v-model="localModelValue.rules.draw.amount"
                label="Max Draws Per Period"
                :readonly="readonly"
                hide-details
              />
            </v-col>
          </v-row>
          <v-divider class="mt-3" />
          <v-row>
            <v-col
              cols="12"
              class="pb-0"
            >
              <h4>Contribution to prize pool</h4>
            </v-col>
            <v-col
              cols="4"
              class="pt-0"
            >
              <v-text-field
                v-model.number="localModelValue.rules.contribution.amount"
                type="number"
                min="0"
                label="Contribution rate, sum (€)"
                :disabled="!!localModelValue.rules.contribution.percent"
                :readonly="readonly"
                hide-details
                @input="removeOnFalsy(localModelValue.rules.contribution, 'amount', $event)"
              />
            </v-col>
            <v-col
              cols="4"
              class="pt-0"
            >
              <v-text-field
                :value="round(localModelValue.rules.contribution.percent * 100, 4)"
                type="number"
                min="0"
                max="100"
                label="Contribution rate, %"
                :disabled="!!localModelValue.rules.contribution.amount"
                :readonly="readonly"
                hide-details
                @input="percentageContributionFmt"
              >
                <template #prepend>
                  <v-btn
                    disabled
                    rounded
                    x-small
                    fab
                  >
                    OR
                  </v-btn>
                </template>
              </v-text-field>
            </v-col>
            <v-col
              v-if="false"
              cols="4"
              class="pt-0"
            >
              <v-text-field
                v-model="localModelValue.rules.contribution.rounds"
                type="number"
                min="0"
                label="Rounds"
                :readonly="readonly"
                hide-details
              />
            </v-col>
          </v-row>
          <v-row
            v-for="(total, index) in localModelValue.rules.contribution.min_bet"
            :key="`contribution-${total.currency}`"
          >
            <v-col cols="4">
              <v-text-field
                v-model="total.amount"
                label="Minimum Bet sum"
                :readonly="readonly"
                hide-details
              />
            </v-col>
            <v-col
              class="d-flex align-end"
              cols="3"
            >
              <v-select
                v-model="total.currency"
                class="undisable-selection"
                label="Currency"
                :items="allCurrencyOptionsVSelectFormatted"
                :disabled="total.currency === 'EUR'"
                :item-disabled="item => selectedContributionCurrencies.includes(item.value)"
                :readonly="readonly"
                hide-details
              />
              <v-btn
                v-if="total.currency !== 'EUR'"
                class="ml-2"
                color="error"
                icon
                :disabled="readonly"
                @click="localModelValue.rules.contribution.min_bet.splice(index, 1)"
              >
                <v-icon>
                  mdi-minus-circle-outline
                </v-icon>
              </v-btn>
              <v-btn
                v-if="!!availableContributionOptions.length"
                class="ml-2"
                color="primary"
                icon
                :disabled="readonly"
                @click="localModelValue.rules.contribution.min_bet.push({ amount: 0, currency: availableContributionOptions[0] })"
              >
                <v-icon>
                  mdi-plus-circle-outline
                </v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-container>
      </v-card>

      <v-card
        :is-loading="isLoading"
        class="mt-2"
      >
        <v-card-title>
          Jackpot Bonus settings (Required)
        </v-card-title>
        <v-divider />
        <v-container>
          <v-row>
            <v-col cols="3">
              <v-text-field
                v-model="localModelValue.rules.bonus.name"
                label="Bonus Name"
                :readonly="readonly"
                hide-details
              />
            </v-col>
            <v-col cols="3">
              <v-select
                v-model="localModelValue.rules.bonus.type"
                :items="BONUS_TYPE_OPTIONS"
                label="Bonus Type"
                :readonly="readonly"
                hide-details
              />
            </v-col>
            <v-col
              v-if="!isCashBonusType"
              class="ml-auto text-right"
              cols="3"
            >
              <v-switch
                v-model="localModelValue.rules.bonus.sticky"
                label="Sticky Bonus"
                color="primary"
                :readonly="readonly"
                hide-details
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="3">
              <v-text-field
                v-model="localModelValue.rules.bonus.wager"
                type="number"
                min="0"
                label="Wager"
                :readonly="readonly"
                hide-details
              />
            </v-col>
            <v-col cols="3">
              <v-select
                v-model="localModelValue.rules.bonus.rollover_type"
                :items="BONUS_ROLLOVER_TYPE_OPTIONS"
                label="Rollover Type"
                :readonly="readonly"
                hide-details
              />
            </v-col>
            <v-col
              v-if="!isCashBonusType"
              cols="3"
            >
              <v-select
                v-model="localModelValue.rules.bonus.status"
                :items="BONUS_STATUS_OPTIONS"
                label="Status"
                :readonly="readonly"
                hide-details
              />
            </v-col>
          </v-row>
          <v-divider />

          <v-row
            v-if="!isCashBonusType"
          >
            <v-col cols="4">
              <v-switch
                :input-value="maxReleaseSwitchState === 'currency'"
                label="Maximum bonus release by currency"
                color="primary"
                :readonly="readonly"
                hide-details
                @change="onMaxReleaseToggle"
              />
            </v-col>
          </v-row>
          <template
            v-if="!isCashBonusType"
          >
            <v-row
              v-if="maxReleaseSwitchState === 'currency'"
              v-for="(total, index) in localModelValue.rules.bonus.max_release"
              :key="`bonus-max-release-${total.currency}`"
            >
              <v-col cols="4">
                <v-text-field
                  v-model.number="total.amount"
                  label="Max release"
                  :readonly="readonly"
                  hide-details
                />
              </v-col>
              <v-col
                class="d-flex align-end"
                cols="3"
              >
                <v-select
                  v-model="total.currency"
                  class="undisable-selection"
                  label="Currency"
                  :items="allCurrencyOptionsVSelectFormatted"
                  :disabled="total.currency === 'EUR'"
                  :item-disabled="item => selectedMaxReleaseCurrencies.includes(item.value)"
                  :readonly="readonly"
                  hide-details
                />
                <v-btn
                  v-if="total.currency !== 'EUR'"
                  class="ml-2"
                  color="error"
                  icon
                  :disabled="readonly"
                  @click="localModelValue.rules.bonus.max_release.splice(index, 1)"
                >
                  <v-icon>
                    mdi-minus-circle-outline
                  </v-icon>
                </v-btn>
                <v-btn
                  v-if="!!availableMaxReleaseOptions.length"
                  class="ml-2"
                  color="primary"
                  icon
                  :disabled="readonly"
                  @click="localModelValue.rules.bonus.max_release.push({ amount: 0, currency: availableMaxReleaseOptions[0] })"
                >
                  <v-icon>
                    mdi-plus-circle-outline
                  </v-icon>
                </v-btn>
              </v-col>
            </v-row>
            <v-row
              v-if="maxReleaseSwitchState === 'multiplier'"
            >
              <v-col
                cols="4"
              >
                <v-text-field
                  v-model.number="localModelValue.rules.bonus.max_release_multiplier"
                  type="number"
                  min="0"
                  label="Max release multiplier"
                  :readonly="readonly"
                  hide-details
                />
              </v-col>
            </v-row>
          </template>

          <v-divider />

          <v-row
            v-if="!isCashBonusType"
          >
            <v-col
              cols="12"
              class="pb-0"
            >
              <h4>Lifetime period</h4>
            </v-col>
            <v-col
              cols="4"
              class="pt-0"
            >
              <v-text-field
                v-model="localModelValue.rules.bonus.lifetime.amount"
                label="Number"
                type="number"
                min="0"
                :readonly="readonly"
                hide-details
              />
            </v-col>
            <v-col
              cols="4"
              class="pt-0"
            >
              <v-select
                v-model="localModelValue.rules.bonus.lifetime.period"
                label="Period"
                :items="LIFETIME_PERIOD_OPTIONS"
                :readonly="readonly"
                hide-details
              />
            </v-col>
          </v-row>
        </v-container>
      </v-card>

      <v-card
        class="mt-2"
        :is-loading="isLoading"
      >
        <v-card-title>
          Game Types, Games and Providers (Optional)
        </v-card-title>
        <v-divider />
        <v-container>
          <v-row>
            <v-col cols="12" class="py-0">
              <h4>
                Game Types
              </h4>
            </v-col>
            <v-col
              class="d-flex align-end line-height-44"
              cols="8"
            >
              Include:
              <v-autocomplete
                v-model="localModelValue.rules.game_types.include"
                class="ml-2 mt-0 pt-0"
                color="primary"
                :items="GAME_TYPE_OPTIONS"
                :disabled="!!localModelValue.rules.game_types.exclude.length"
                multiple
                chips
                deletable-chips
                :clearable="!readonly"
                :readonly="readonly"
                hide-details
              >
                <template #selection="{item, select, attrs, selected, index}">
                  <v-chip
                    v-bind="attrs"
                    color="primary"
                    :input-value="selected"
                    outlined
                    close
                    :disabled="readonly"
                    @click="select"
                    @click:close="localModelValue.rules.game_types.include.splice(index, 1)"
                  >
                    {{ item.text }}
                  </v-chip>
                </template>
              </v-autocomplete>
            </v-col>
            <v-col
              class="d-flex align-end line-height-44"
              cols="8"
            >
              Exclude:
              <v-autocomplete
                v-model="localModelValue.rules.game_types.exclude"
                class="ml-2 mt-0 pt-0"
                color="error"
                :items="GAME_TYPE_OPTIONS"
                :disabled="!!localModelValue.rules.game_types.include.length"
                multiple
                chips
                deletable-chips
                :clearable="!readonly"
                :readonly="readonly"
                hide-details
              >
                <template #selection="{item, select, attrs, selected, index}">
                  <v-chip
                    v-bind="attrs"
                    color="error"
                    :input-value="selected"
                    outlined
                    close
                    :disabled="readonly"
                    @click="select"
                    @click:close="localModelValue.rules.game_types.exclude.splice(index, 1)"
                  >
                    {{ item.text }}
                  </v-chip>
                </template>
              </v-autocomplete>
            </v-col>
          </v-row>

          <v-divider />

          <v-row class="mt-3">
            <v-col cols="12" class="py-0">
              <h4>
                Providers
              </h4>
            </v-col>
            <v-col
              class="d-flex align-end line-height-44"
              cols="8"
            >
              Include:
              <v-autocomplete
                v-model="localModelValue.rules.brands.include"
                class="ml-2 mt-0 pt-0"
                color="primary"
                :items="brands"
                item-text="name"
                item-value="id"
                :disabled="!!localModelValue.rules.brands.exclude.length"
                multiple
                chips
                deletable-chips
                :clearable="!readonly"
                :readonly="readonly"
                hide-details
              >
                <template #selection="{item, select, attrs, selected, index}">
                  <v-chip
                    v-if="index < CHIP_LIST_OUTPUT_LIMIT"
                    v-bind="attrs"
                    color="primary"
                    :input-value="selected"
                    outlined
                    close
                    :disabled="readonly"
                    @click="select"
                    @click:close="localModelValue.rules.brands.include.splice(index, 1)"
                  >
                    {{ item.name }}
                  </v-chip>
                  <v-chip
                    v-if="index === CHIP_LIST_OUTPUT_LIMIT"
                    class="more"
                    color="primary"
                    @click.capture.stop="chipPopupKey = 'brands:include'"
                  >
                    +{{ localModelValue.rules.brands.include.length - CHIP_LIST_OUTPUT_LIMIT }} more
                  </v-chip>
                </template>
              </v-autocomplete>
            </v-col>
            <v-col
              class="d-flex align-end line-height-44"
              cols="8"
            >
              Exclude:
              <v-autocomplete
                v-model="localModelValue.rules.brands.exclude"
                class="ml-2 mt-0 pt-0"
                color="error"
                :items="brands"
                item-text="name"
                item-value="id"
                :disabled="!!localModelValue.rules.brands.include.length"
                multiple
                chips
                deletable-chips
                :clearable="!readonly"
                :readonly="readonly"
                hide-details
              >
                <template #selection="{item, select, attrs, selected, index}">
                  <v-chip
                    v-if="index < CHIP_LIST_OUTPUT_LIMIT"
                    v-bind="attrs"
                    color="error"
                    :input-value="selected"
                    outlined
                    close
                    :disabled="readonly"
                    @click="select"
                    @click:close="localModelValue.rules.brands.exclude.splice(index, 1)"
                  >
                    {{ item.name }}
                  </v-chip>
                  <v-chip
                    v-if="index === CHIP_LIST_OUTPUT_LIMIT"
                    class="more"
                    color="error"
                    @click.capture.stop="chipPopupKey = 'brands:exclude'"
                  >
                    +{{ localModelValue.rules.brands.exclude.length - CHIP_LIST_OUTPUT_LIMIT }} more
                  </v-chip>
                </template>
              </v-autocomplete>
            </v-col>
          </v-row>

          <v-divider />

          <v-row class="mt-3">
            <v-col cols="12" class="pt-0">
              <h4>
                Games
              </h4>
            </v-col>
            <v-col cols="12" class="pb-0">
              <v-combobox
                label="Brands (filter)"
                auto-select-first
                v-model="brandFilter"
                :items="brandOptions"
                hint="Filter out brands for the below game selects"
                persistent-hint
                multiple
                clearable
                class="col-5 pl-0"
                dense
                :readonly="readonly"
                prepend-icon="mdi-filter"
              />
            </v-col>
            <v-col
              class="d-flex align-end line-height-44"
              cols="8"
            >
              Include:
              <v-autocomplete
                v-model="localModelValue.rules.games.include"
                class="ml-2 mt-0 pt-0"
                color="primary"
                item-text="name"
                item-value="game_id"
                :items="includeGameOptionsWithFilter"
                :disabled="!!localModelValue.rules.games.exclude.length"
                multiple
                chips
                deletable-chips
                :menu-props="{ closeOnClick: true }"
                :clearable="!readonly"
                :readonly="readonly"
                hide-details
              >
                <template #selection="{item, select, attrs, selected, index}">
                  <v-chip
                    v-if="index < CHIP_LIST_OUTPUT_LIMIT"
                    v-bind="attrs"
                    color="primary"
                    :input-value="selected"
                    outlined
                    close
                    :disabled="readonly"
                    @click="select"
                    @click:close="localModelValue.rules.games.include.splice(index, 1)"
                  >
                    {{ item.name }}
                  </v-chip>
                  <v-chip
                    v-if="index === CHIP_LIST_OUTPUT_LIMIT"
                    class="more"
                    color="primary"
                    @click.capture.stop="chipPopupKey = 'games:include'"
                  >
                    +{{ localModelValue.rules.games.include.length - CHIP_LIST_OUTPUT_LIMIT }} more
                  </v-chip>
                </template>
                <template #item="{attrs, on, item}">
                  <v-list-item v-bind="attrs" v-on="on">
                    <v-list-item-action>
                      <v-simple-checkbox
                        :value="attrs.inputValue"
                        v-on="on"
                      />
                    </v-list-item-action>

                    <v-list-item-content>
                      <v-list-item-title>
                        <span v-html="item.name"></span>
                        <small class="float-right">({{ item.system_name }} {{ item.game_id }})</small>
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </v-autocomplete>
            </v-col>
            <v-col
              class="d-flex align-end line-height-44"
              cols="8"
            >
              Exclude:
              <v-autocomplete
                v-model="localModelValue.rules.games.exclude"
                class="ml-2 mt-0 pt-0"
                color="error"
                item-text="name"
                item-value="game_id"
                :items="excludeGameOptionsWithFilter"
                :disabled="!!localModelValue.rules.games.include.length"
                multiple
                chips
                deletable-chips
                :menu-props="{ closeOnClick: true }"
                :clearable="!readonly"
                :readonly="readonly"
                hide-details
              >
                <template #selection="{item, select, attrs, selected, index}">
                  <v-chip
                    v-if="index < CHIP_LIST_OUTPUT_LIMIT"
                    v-bind="attrs"
                    color="error"
                    :input-value="selected"
                    outlined
                    close
                    :disabled="readonly"
                    @click="select"
                    @click:close="localModelValue.rules.games.exclude.splice(index, 1)"
                  >
                    {{ item.name }}
                  </v-chip>
                  <v-chip
                    v-if="index === CHIP_LIST_OUTPUT_LIMIT"
                    class="more"
                    color="error"
                    @click.capture.stop="chipPopupKey = 'games:exclude'"
                  >
                    +{{ localModelValue.rules.games.exclude.length - CHIP_LIST_OUTPUT_LIMIT }} more
                  </v-chip>
                </template>
                <template #item="{attrs, on, item}">
                  <v-list-item v-bind="attrs" v-on="on">
                    <v-list-item-action>
                      <v-simple-checkbox
                        :value="attrs.inputValue"
                        v-on="on"
                      />
                    </v-list-item-action>

                    <v-list-item-content>
                      <v-list-item-title>
                        <span v-html="item.name"></span>
                        <small class="float-right">({{ item.system_name }} {{ item.game_id }})</small>
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </v-autocomplete>
            </v-col>
          </v-row>
        </v-container>
      </v-card>

      <v-card
        class="mt-2"
        :is-loading="isLoading"
      >
        <v-card-title>
          Segments (Optional)
        </v-card-title>
        <v-divider />
        <v-container>
          <v-row
            v-for="(rule, index) in filterGroups.general"
            :key="rule._key"
          >
            <v-col cols="4">
              <v-select
                v-model="rule.field"
                label="Attribute"
                :items="SEGMENT_FIELDS"
                :item-disabled="item => getIsRuleFieldOptionDisabled(item, rule)"
                :readonly="readonly"
                hide-details
              />
            </v-col>
            <v-col cols="4">
              <v-select
                v-model="rule.equation"
                label="Equation"
                :items="segmentEquationOptionsVSelectFormatted"
                :item-disabled="item => getIsRuleEquationOptionDisabled(item, rule)"
                :readonly="readonly"
                hide-details
              />
            </v-col>
            <v-col
              cols="4"
              class="d-flex align-end"
            >
              <v-text-field
                v-model="rule.value"
                label="Value"
                :readonly="readonly"
                hide-details
              />
              <v-btn
                v-if="filterGroups.general.length === 1 && (!!rule.field || !!rule.value)"
                class="ml-2"
                color="error"
                icon
                :disabled="readonly"
                @click="Object.assign(rule, { field: '', equation: '=', value: '' })"
              >
                <v-icon>
                  mdi-close-circle-outline
                </v-icon>
              </v-btn>
              <v-btn
                v-if="filterGroups.general.length > 1"
                class="ml-2"
                color="error"
                icon
                :disabled="readonly"
                @click="removeSegmentationEntry(rule._key)"
              >
                <v-icon>
                  mdi-minus-circle-outline
                </v-icon>
              </v-btn>
              <v-btn
                class="ml-2"
                color="primary"
                icon
                :disabled="readonly"
                @click="localModelValue.rules.filter.push({ field: '', equation: '=', value: '', _key: `filter-rule-${Math.random()}-${Date.now()}-${index}` })"
              >
                <v-icon>
                  mdi-plus-circle-outline
                </v-icon>
              </v-btn>
            </v-col>
          </v-row>

          <v-row
            v-for="(userFilter, index) in filterGroups.userId"
            :key="userFilter.field + userFilter.equation + index"
          >
            <v-col cols="10">
              <v-combobox
                v-model="userFilter.value"
                clearable
                hide-selected
                hide-details
                attach
                :label="userFilter.equation === SEGMENT_IN ? 'Included Users\' IDs' : 'Excluded Users\' IDs'"
                multiple
                persistent-hint
                small-chips
                :readonly="readonly"
                counter
              >
                <template #no-data>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title>
                        Press <kbd>enter</kbd> to create a new one
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </template>

                <template #selection="{ attrs, item, select, selected, index }">
                  <v-chip
                    :input-value="selected"
                    outlined
                    close
                    v-bind="attrs"
                    :disabled="readonly"
                    @click="select"
                    @click:close="userFilter.value.splice(index, 1)"
                  >
                    {{ item }}
                  </v-chip>
                </template>
              </v-combobox>
            </v-col>
            <v-col cols="2">
              <v-btn
                v-text="'Upload'"
                color="primary"
                :disabled="readonly"
                @click="$refs[`import-${userFilter.equation}-${index}`][0].value = null, $refs[`import-${userFilter.equation}-${index}`][0].click()"
              />
              <input
                :ref="`import-${userFilter.equation}-${index}`"
                type="file"
                hidden
                accept=".csv"
                @change="onImportChange($event, userFilter)"
              >
            </v-col>
          </v-row>
        </v-container>
      </v-card>

      <v-dialog
        v-model="isChipPopupDisplayed"
        max-width="600"
      >
        <v-card
          v-if="isChipPopupDisplayed"
        >
          <v-card-title>
            Selected {{ chipPopupRecord }} ({{ localModelValue.rules[chipPopupRecord][chipPopupFilter].length }})
            <v-btn
              class="ml-2"
              outlined
              rounded
              :color="chipPopupFilter === 'include' ? 'primary' : 'error'"
              @click="localModelValue.rules[chipPopupRecord][chipPopupFilter] = [], isChipPopupDisplayed = false"
            >
              Clear All
            </v-btn>
          </v-card-title>
          <v-divider />
          <v-text-field
            v-model="chipPopupSearch"
            class="px-3"
            placeholder="Search"
            prepend-icon="mdi-magnify"
            solo
            flat
            clearable
            hide-details
          />
          <v-divider />
          <v-row class="mx-0 pa-3" style="gap: 0.5rem;">
            <v-chip
              v-for="(recordId, index) in localModelValue.rules[chipPopupRecord][chipPopupFilter]"
              v-if="getChipRecordLabel(recordId).toLowerCase().includes(chipSearchWrapSafe.toLowerCase())"
              :key="recordId"
              outlined
              close
              :color="chipPopupFilter === 'include' ? 'primary' : 'error'"
              :disabled="readonly"
              @click:close="localModelValue.rules[chipPopupRecord][chipPopupFilter].splice(index, 1)"
            >
              {{ getChipRecordLabel(recordId) }}
            </v-chip>
          </v-row>
        </v-card>
      </v-dialog>
    </v-container>
  </div>
</template>

<script>
import { DrawerDialogControls } from '@/components/core/DrawerDialog'
import SelectProject from '@/components/core/SelectProject/SelectProject.vue'
import DatePicker from '@/components/core/DatePicker/DatePicker.vue'
import { map, difference, cloneDeep, omit, defaults, round } from 'lodash'
import {
  SEGMENT_FIELDS,
  SEGMENT_EQUATION,
  JACKPOT_TYPE_OPTIONS,
  STATUS_ACTIVE,
  STATUS_FINISHED,
  getBonusDefaults,
  ACCOUNT_ID_FILTER,
  SEGMENT_IN,
  SEGMENT_NOT_IN,
} from '../consts'
import { parseCSV } from '@/utils/helper'

const DRAW_PERIOD_OPTIONS = ['hours', 'days', 'weeks', 'months']
const BONUS_STATUS_OPTIONS = [
  { text: 'Active', value: 'active' },
  { text: 'New', value: 'new' },
]

const BONUS_TYPE = {
  casino: 'casino',
  cash: 'cash'
}

const BONUS_TYPE_OPTIONS = [
  { text: 'Bonus Money', value: BONUS_TYPE.casino },
  { text: 'Cash', value: BONUS_TYPE.cash },
]

const CASH_TYPE_BONUS_EXCLUDED_FIELDS = ['status', 'sticky', 'max_release', 'max_release_multiplier', 'lifetime']

const BONUS_ROLLOVER_TYPE_OPTIONS = [
  { text: 'Bonus', value: 'bonus' },
  { text: 'Cash', value: 'cash' },
]

const CHIP_LIST_OUTPUT_LIMIT = 2
const SPACE_CHAR = ' '
const DATE_READONLY_HINT = 'Start date cannot be edited for Active jackpot'
const DATE_DISABLED_HINT = 'Dates cannot be edited for Finished jackpot'

const DATE_FINISH_BEFORE_START_HINT = 'End date cannot be earlier than start date'
const DATE_FINISH_BEFORE_NOW_HINT = 'End date cannot be earlier than the current date and time'

const GAME_TYPES = {
  slots: 'Slots',
  roulette: 'Roulette',
  'live-casino': 'Live casino',
  sports: 'Sports',
  'virtual-sports': 'Virtual sports',
  'table-games': 'Table games',
  blackjack: 'Blackjack',
  baccarat: 'Baccarat',
  poker: 'Poker',
  bingo: 'Bingo',
  scratch: 'Scratch',
  lottery: 'Lottery',
  sicbo: 'Sicbo',
  dice: 'Dice',
  crash: 'Crash',
  other: 'Other',
}

const LIST_SEGMENTS = [SEGMENT_IN, SEGMENT_NOT_IN]

export default {
  components: {
    DrawerDialogControls,
    SelectProject,
    DatePicker,
  },
  props: {
    jackpot: {
      type: Object,
      required: true,
    },
    games: {
      type: Array,
      required: true,
    },
    gameNameById: {
      type: Object,
      required: true,
    },
    brandNameById: {
      type: Object,
      required: true,
    },
    brands: {
      type: Array,
      required: true,
    },
    currencies: {
      type: Array,
      required: true,
    },
    isLoading: Boolean,
    readonly: Boolean,
  },
  data() {
    const [startDate, startTime] = !!this.jackpot.start ? this.jackpot.start.split(SPACE_CHAR) : ['', ''];
    const [endDate, endTime] = !!this.jackpot.end ? this.jackpot.end.split(SPACE_CHAR) : ['', ''];

    return {
      localModelValue: this.cloneWithPrep(this.jackpot),
      isPristine: true,
      JACKPOT_TYPE_OPTIONS,
      DRAW_PERIOD_OPTIONS,
      LIFETIME_PERIOD_OPTIONS: DRAW_PERIOD_OPTIONS,
      BONUS_STATUS_OPTIONS,
      BONUS_TYPE_OPTIONS,
      BONUS_ROLLOVER_TYPE_OPTIONS,
      GAME_TYPE_OPTIONS: Object.keys(GAME_TYPES).map(key => ({ text: GAME_TYPES[key], value: key })),
      SEGMENT_EQUATION,
      SEGMENT_FIELDS,
      SEGMENT_IN,
      CHIP_LIST_OUTPUT_LIMIT,
      STATUS_ACTIVE,
      STATUS_FINISHED,
      DATE_READONLY_HINT,
      DATE_DISABLED_HINT,
      brandFilter: [],
      chipPopupKey: '',
      chipPopupSearch: '',

      // DatePicker accepts time and date as separate params
      // We need to break down the dates into their parts and combine them on save
      dateComponents: {
        start: {
          date: startDate,
          time: startTime,
        },
        end: {
          date: endDate,
          time: endTime,
        },
      },
    }
  },
  emits: ['save', 'close'],

  mounted() {
    const unwatch = this.$watch('localModelValue', () => {
      this.isPristine = false
      watchDone()
    }, { deep: true })
    const dateComponentsUnwatch = this.$watch('dateComponents', () => {
      this.isPristine = false
      watchDone()
    }, { deep: true })

    const watchDone = () => {
      unwatch()
      dateComponentsUnwatch()
    }
  },

  computed: {
    dialogTitle() {
      return 'jackpot_id' in this.jackpot ? `Edit Jackpot ${this.jackpot.name}` : 'Add new Jackpot'
    },
    selectedContributionCurrencies() {
      return map(this.localModelValue.rules.contribution.min_bet, 'currency')
    },
    selectedPrizepoolCurrencies() {
      return map(this.localModelValue.rules.prizepool, 'currency')
    },
    selectedMaxReleaseCurrencies() {
      return map(this.localModelValue.rules.bonus.max_release, 'currency')
    },
    allCurrencyOptionsVSelectFormatted() {
      return this.currencies.map(currency => ({text: currency, value: currency}))
    },
    availableContributionOptions() {
      return difference(this.currencies, this.selectedContributionCurrencies)
    },
    availablePrizepoolOptions() {
      return difference(this.currencies, this.selectedPrizepoolCurrencies)
    },
    availableMaxReleaseOptions() {
      return difference(this.currencies, this.selectedMaxReleaseCurrencies)
    },
    segmentEquationOptionsVSelectFormatted() {
      return SEGMENT_EQUATION.map(equation => ({ text: equation, value: equation }))
    },
    filterGroups() {
      const userId = []
      const general = []
      for (const filter of this.localModelValue.rules.filter) {
        if (filter.field === ACCOUNT_ID_FILTER && LIST_SEGMENTS.includes(filter.equation)) {
          if (typeof filter.value === 'string') {
            filter.value = filter.value.split(',')
          }
          userId.push(filter)
        }
        else {
          general.push(filter)
        }
      }

      // Order user filters consistently in all configs
      userId.sort((a, b) => (b.equation === SEGMENT_IN) - (a.equation === SEGMENT_IN))
      return { general, userId }
    },

    isCashBonusType() {
      return this.localModelValue.rules.bonus.type === BONUS_TYPE.cash
    },

    maxReleaseSwitchState() {
      return !!this.localModelValue.rules.bonus.max_release?.length ? 'currency' : 'multiplier'
    },

    brandOptions() {
      const set = new Set()
      for (const game of this.games) {
        set.add(game.brand.alias)
      }

      return [...set]
    },

    includeGameOptionsWithFilter() {
      return this.games.filter(game => {
        const forceInclude = this.localModelValue.rules.games.include.includes(game.game_id)
        if (forceInclude) {
          return true
        }

        if (this.brandFilter.length) {
          return this.brandFilter.includes(game.brand.alias)
        }

        return true
      })
    },

    excludeGameOptionsWithFilter() {
      return this.games.filter(game => {
        const forceInclude = this.localModelValue.rules.games.exclude.includes(game.game_id)
        if (forceInclude) {
          return true
        }

        if (this.brandFilter.length) {
          return this.brandFilter.includes(game.brand.alias)
        }

        return true
      })
    },

    isChipPopupDisplayed: {
      get() {
        return !!this.chipPopupKey
      },

      set(value) {
        if (!value) {
          this.chipPopupKey = ''
        }
      },

    },

    chipPopupRecord() {
      return (this.chipPopupKey || ':').split(':')[0]
    },
    chipPopupFilter() {
      return (this.chipPopupKey || ':').split(':')[1]
    },

    chipSearchWrapSafe() {
      return typeof this.chipPopupSearch === 'string' ? this.chipPopupSearch : ''
    },

    endDateValidity() {
      const now = new Date().getTime()
      const start = new Date(`${this.dateComponents.start.date} ${this.dateComponents.start.time}`).getTime()
      const end = new Date(`${this.dateComponents.end.date} ${this.dateComponents.end.time}`).getTime()

      if (now > end) {
        return 'now'
      }

      if (start > end) {
        return 'start'
      }

      return 'ok'
    },

    endDateHint() {
      const statusHint = this.getDateHint()
      if (statusHint) {
        return statusHint
      }

      switch (this.endDateValidity) {
        case 'now':
          return DATE_FINISH_BEFORE_NOW_HINT
        case 'start':
          return DATE_FINISH_BEFORE_START_HINT
        case 'ok':
          return ''
      }
    },
  },

  methods: {
    round,
    removeOnFalsy(obj, key, val) {
      if (!val) {
        this.$delete(obj, key)
      }
    },
    percentageContributionFmt(val) {
      if (val) {
        this.$set(this.localModelValue.rules.contribution, 'percent', round(val / 100, 4))
      } else {
        this.removeOnFalsy(this.localModelValue.rules.contribution, 'percent', val)
      }
    },
    onMaxReleaseToggle(val) {
      if (!val) {
        this.$delete(this.localModelValue.rules.bonus, 'max_release')
        this.$set(this.localModelValue.rules.bonus, 'max_release_multiplier', 0)
      } else {
        this.$delete(this.localModelValue.rules.bonus, 'max_release_multiplier')
        this.$set(this.localModelValue.rules.bonus, 'max_release', [
          { currency: 'EUR', amount: 0 },
        ])
      }
    },
    cloneWithPrep(jackpot) {
      const localModelValue = cloneDeep(jackpot)
      // Fields that the API removes if empty but required for UI
      for (const includeExcludeFilter of ['games', 'brands', 'game_types']) {
        if (!localModelValue.rules[includeExcludeFilter]) {
          localModelValue.rules[includeExcludeFilter] = {}
        }

        for (const filterType of ['include', 'exclude']) {
          if (!localModelValue.rules[includeExcludeFilter][filterType]) {
            localModelValue.rules[includeExcludeFilter][filterType] = []
          }
        }
      }

      localModelValue.rules.filter ??= []

      const userIdIncludeFilter = localModelValue.rules.filter.find(filter => filter.field === ACCOUNT_ID_FILTER && filter.equation === SEGMENT_IN)
      const userIdExcludeFilter = localModelValue.rules.filter.find(filter => filter.field === ACCOUNT_ID_FILTER && filter.equation === SEGMENT_NOT_IN)
      const needsDummy = !(localModelValue.rules.filter.length - Number(!!userIdIncludeFilter) - Number(!!userIdExcludeFilter))
      if (!userIdIncludeFilter) {
        localModelValue.rules.filter.push({
          field: ACCOUNT_ID_FILTER,
          equation: SEGMENT_IN,
          value: [],

          _key: `filter-rule-${Math.random()}-${Date.now()}`,
        })
      }
      if (!userIdExcludeFilter) {
        localModelValue.rules.filter.push({
          field: ACCOUNT_ID_FILTER,
          equation: SEGMENT_NOT_IN,
          value: [],

          _key: `filter-rule-${Math.random()}-${Date.now()}`,
        })
      }
      if (needsDummy) {
        localModelValue.rules.filter.push({
          field: '',
          equation: '=',
          value: '',

          _key: `filter-rule-${Math.random()}-${Date.now()}`,
        })
      }

      for (const rule of localModelValue.rules.filter) {
        if (!('_key' in rule)) {
          rule._key = `filter-rule-${Math.random()}-${Date.now()}`
        }
      }

      const mayNeedBonusDefaults = localModelValue.rules.bonus.type === BONUS_TYPE.cash

      if (mayNeedBonusDefaults) {
        localModelValue.rules.bonus = defaults(localModelValue.rules.bonus, getBonusDefaults())
      }

      return localModelValue
    },
    saveWithFmt() {
      this.$emit('save', {
        ...this.localModelValue,
        start: `${this.dateComponents.start.date} ${this.dateComponents.start.time}`,
        end: `${this.dateComponents.end.date} ${this.dateComponents.end.time}`,
        rules: {
          ...this.localModelValue.rules,
          bonus: omit(this.localModelValue.rules.bonus, this.isCashBonusType ? CASH_TYPE_BONUS_EXCLUDED_FIELDS : []),
          filter: this.localModelValue.rules.filter.filter(filter => !!filter.field && !!filter.value?.length),
        },
      })
    },

    /**
     *
     * @param {Event & {target: HTMLInputElement}} event
     * @param {{equation: 'in' | 'notin', value: string[], field: 'account.id'}} filter
     */
    onImportChange (event, filter) {
      if (!event.target.files.length) {
        return
      }

      const file = event.target.files[0]

      const reader = new FileReader()
      reader.readAsText(file, 'utf-8')
      reader.onload = (event) => {
        try {

          let records = parseCSV(event.target.result)

          if (!('account_id' in records[0])) {

            this.$showError('Could not find column accound_id in results')

          } else {

            for (const record of records) {
              if (!filter.value.includes(record.account_id)) {
                filter.value.push(record.account_id)
              }
            }

            this.$showSuccess('Successfully imported columns')
          }

        } catch (err) {
          this.$showError(`Failed to parse CSV, err: ${err}`)
        }
      }
    },

    getChipRecordLabel(recordId) {
      switch (this.chipPopupRecord) {
        case 'games':
          return this.gameNameById[recordId]
        case 'brands':
          return this.brandNameById[recordId]
        default:
          return recordId
      }
    },

    removeSegmentationEntry(key) {
      const index = this.localModelValue.rules.filter.findIndex(segment => segment._key === key)

      if (index !== -1) {
        this.localModelValue.rules.filter.splice(index, 1)
      }
    },

    getDateHint(isStart = false) {
      if (this.localModelValue.status === STATUS_FINISHED) {
        return DATE_DISABLED_HINT
      }

      if (this.localModelValue.status === STATUS_ACTIVE && isStart) {
        return DATE_READONLY_HINT
      }

      return ''
    },

    getIsRuleFieldOptionDisabled(option, rule) {
      return option.value === ACCOUNT_ID_FILTER && LIST_SEGMENTS.includes(rule.equation)
    },

    getIsRuleEquationOptionDisabled(option, rule) {
      return LIST_SEGMENTS.includes(option.value) && rule.field === ACCOUNT_ID_FILTER
    },
  },
}
</script>

<style lang="scss" scoped>
.undisable-selection ::v-deep .v-select__selection {
  color: inherit !important;
}
.dialog-controls {
  position: sticky;
  top: 0;
  left: 0;
  z-index: 1;
}
</style>
